import { useApolloClient } from "@apollo/client";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuItemWithIcon } from "@msys/ui";
import { CircularProgress, Icon } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React, { useCallback, useState } from "react";
import { XiopdExportDestination } from "../../../../clients/graphqlTypes.js";
import { useGlobalLoadingCounter } from "../../../global-loading-state/useGlobalLoadingCounter.js";
import { useExportAsXiopdMutation } from "./XiopdExport.generated.js";

interface Props {
  docId: string;
  projectId: string;
  title?: string;
  exportDestination: XiopdExportDestination;
}

export const ExportAsXiopdMenuItem = React.forwardRef<HTMLLIElement, Props>(
  (props, ref) => {
    const { t } = useTranslate("QuoteItem");

    const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");

    const { enqueueSnackbar } = useSnackbar();
    const exportAsXiopd = useExportAsXiopd();

    const { increment, decrement } = useGlobalLoadingCounter();

    return (
      <MenuItemWithIcon
        ref={ref}
        icon={
          status !== "loading" ? (
            <Icon>
              <FontAwesomeIcon icon={faFileExport} />
            </Icon>
          ) : (
            <CircularProgress size={"1rem"} />
          )
        }
        onClick={async () => {
          try {
            increment();
            setStatus("loading");
            await exportAsXiopd(
              props.projectId,
              props.docId,
              props.exportDestination
            );
            setStatus("idle");
          } catch (e) {
            if (e instanceof Error)
              enqueueSnackbar(e.message, { variant: "error" });
            Sentry.captureException(e);
            setStatus("error");
          } finally {
            decrement();
          }
        }}
        disabled={status === "loading"}
      >
        {props.title ??
          `${t("Export as")} xi:opd`.concat(
            props.exportDestination === "MSYS" ? " - Intern" : ""
          )}
      </MenuItemWithIcon>
    );
  }
);

const useExportAsXiopd = () => {
  const client = useApolloClient();
  const [exportAsXiopdMutation] = useExportAsXiopdMutation({
    client,
  });

  return useCallback(
    async (
      projectId: string,
      docId: string,
      exportDestination: XiopdExportDestination
    ) => {
      const exportAsXiopdResult = await exportAsXiopdMutation({
        variables: {
          input: { docId, projectId, exportDestination },
        },
      });

      if (exportAsXiopdResult) {
        download(exportAsXiopdResult.data?.exportAsXiopd.downloadUrl ?? "");
      }
    },
    [exportAsXiopdMutation]
  );
};

function download(url: string) {
  const elem = window.document.createElement("a");
  elem.href = url;
  document.body.appendChild(elem);
  elem.click();
  document.body.removeChild(elem);
}
